import { IconButton, SelectionMode, Stack, Text, Toggle, TooltipHost } from '@fluentui/react';
import { ICodeGroupExclusion, ISlidingFeePlan } from 'api/models/slidingFee.model';
import { SortableDetailsList, SubSection } from 'components';
import { EditDetailsColumn } from 'components/EditDetailsColumn';
import useSlidingFee from 'hooks/store/useSlidingFee';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectSelectedProgramPlanRangeExclusionListDeleted,
    selectSelectedRangeExclusionList,
    toggleDeletedRangeExclusions,
} from 'state/slices/sliding-fee/sliding-fee.slice';
import dateOnly from 'utils/dateOnly';
import { v4 as uuid } from 'uuid';
import GroupExclusionPanel from './CodeGroupExlusionPanel';

type Props = {
    programPlan?: ISlidingFeePlan;
    onProgramPlanPropertyChange: (key: keyof ISlidingFeePlan, value: string | number | ICodeGroupExclusion[] | boolean) => void;
};

export default function GroupCodeExclusion(props: Props): JSX.Element {
    const { programPlan, onProgramPlanPropertyChange } = props;
    const { setSelectedGroupExclusion, toggleIsCodeGroupExclusionPanelOpen } = useSlidingFee();
    const isActive = useSelector(selectSelectedProgramPlanRangeExclusionListDeleted);
    const _selectSelectedCodeExclusion = useSelector(selectSelectedRangeExclusionList);

    const dispatch = useDispatch();
    const _onItemInvoked = (item: ICodeGroupExclusion, index?: number) => {
        setSelectedGroupExclusion(item);
        toggleIsCodeGroupExclusionPanelOpen();
    };

    const onRenderCalculationType = (item?: ICodeGroupExclusion) => {
        if (item?.calculationType === 'flatFeeOnly') {
            return <Text variant="smallPlus">Flat Fee Only</Text>;
        }
        if (item?.calculationType === 'percentagePerCode') {
            return <Text variant="smallPlus">Percentage Per Code</Text>;
        }
        if (item?.calculationType === 'flatFeePerCode') {
            return <Text variant="smallPlus">Flat Fee Per Code </Text>;
        } else {
            return '';
        }
    };

    return (
        <SubSection
            title="Code Range Exclusions"
            headingCenterContent={
                <Stack horizontal horizontalAlign="space-between">
                    <Stack.Item>
                        <TooltipHost content="Add Code Range Exclusions">
                            <IconButton
                                iconProps={{ iconName: 'Add' }}
                                onClick={() => {
                                    setSelectedGroupExclusion(newGroupExclusion());
                                    toggleIsCodeGroupExclusionPanelOpen();
                                }}
                            />
                        </TooltipHost>
                    </Stack.Item>
                    <Stack.Item>
                        <Toggle
                            styles={{ root: { marginBottom: 2, minWidth: 150 } }}
                            checked={!!isActive}
                            inlineLabel
                            label="Active Only"
                            onChange={() => {
                                dispatch(toggleDeletedRangeExclusions());
                            }}
                        />
                    </Stack.Item>
                </Stack>
            }
        >
            <GroupExclusionPanel programPlan={programPlan} onProgramPlanPropertyChange={onProgramPlanPropertyChange} />
            {programPlan && programPlan.codeRangeExclusions && programPlan.codeRangeExclusions.length ? (
                <SortableDetailsList
                    selectionMode={SelectionMode.none}
                    items={_selectSelectedCodeExclusion}
                    columns={[
                        {
                            key: 'category',
                            minWidth: 100,
                            maxWidth: 150,
                            fieldName: 'category',
                            name: 'Category',
                            getValueKey: (item?: ICodeGroupExclusion) => {
                                return item?.startCode ?? '';
                            },
                            onRender: (item?: ICodeGroupExclusion, index?: number) => {
                                if (item) {
                                    return (
                                        <EditDetailsColumn
                                            editOnClick={() => _onItemInvoked(item, index)}
                                            title={item ? `${item.startCode} - ${item.endCode}` : ''}
                                        />
                                    );
                                } else {
                                    return undefined;
                                }
                            },
                        },
                        {
                            key: 'calculationType',
                            minWidth: 150,
                            maxWidth: 150,
                            fieldName: 'calculationType',
                            name: 'Calculation Type',
                            onRender: onRenderCalculationType,
                        },
                        {
                            key: 'doesNotSlide',
                            minWidth: 150,
                            maxWidth: 150,
                            fieldName: 'doesNotSlide',
                            name: 'Does Not Slide',
                            onRender: (item?: ICodeGroupExclusion) => {
                                if (item?.doesNotSlide) {
                                    return <Text variant="smallPlus">&#10003;</Text>;
                                }
                            },
                        },
                        {
                            key: 'feePercentage',
                            minWidth: 120,
                            maxWidth: 120,
                            fieldName: 'feePercentage',
                            name: 'Fee or Percentage',
                        },
                        {
                            key: 'labFee',
                            minWidth: 100,
                            maxWidth: 100,
                            fieldName: 'labFee',
                            name: 'Lab Fee',
                        },
                        {
                            key: 'absoluteFee',
                            minWidth: 100,
                            maxWidth: 100,
                            fieldName: 'absoluteFee',
                            name: 'Absolute Fee',
                        },
                        {
                            key: 'effectiveDate',
                            fieldName: 'effectiveDate',
                            minWidth: 100,
                            maxWidth: 250,
                            name: 'Effective Date',
                            onRender: (item?: ICodeGroupExclusion) => {
                                if (item) {
                                    const date = item.effectiveDate ? dateOnly(item.effectiveDate as string) : '';
                                    return <Text variant="smallPlus">{date}</Text>;
                                }
                            },
                        },
                        {
                            key: 'expirationDate',
                            name: 'Expiration Date',
                            minWidth: 100,
                            maxWidth: 100,
                            fieldName: 'expirationDate',
                            onRender: (item?: ICodeGroupExclusion) => {
                                if (item) {
                                    const date = item.expirationDate ? dateOnly(item.expirationDate as string) : '';
                                    return <Text variant="smallPlus">{date}</Text>;
                                }
                            },
                        },
                    ]}
                />
            ) : null}
        </SubSection>
    );
}

function newGroupExclusion(): ICodeGroupExclusion {
    return {
        id: uuid(),
        isDeleted: false,
        absoluteFee: 0,
        calculationType: '',
        doesNotSlide: false,
        effectiveDate: '',
        expirationDate: '',
        feePercentage: 0,
        labFee: 0,
        startCode: '',
        endCode: '',
    };
}
